// as-cetra-vet-ui/src/components/authenticated/handout/customise-mode/EditableList.js

import React, { useState } from 'react';
import { Box, List, ListItem, ListItemIcon, ListItemText, TextField, IconButton } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmRemoveDialog from './ConfirmRemoveDialog';
import { useCustomiseMode } from './CustomiseModeContext';

const EditableList = ({ block, sectionIndex, blockIndex, children }) => {
    const { isCustomiseMode, updateBlock } = useCustomiseMode();
    const [editingItemIndex, setEditingItemIndex] = useState(null);
    const [editedValue, setEditedValue] = useState('');
    const [hoveredItemIndex, setHoveredItemIndex] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [itemToRemove, setItemToRemove] = useState(null);

    if (!isCustomiseMode) {
        return children;
    }

    const handleStartEdit = (itemIndex) => {
        setEditingItemIndex(itemIndex);
        setEditedValue(block.values[itemIndex]);
    };

    const handleSaveEdit = () => {
        const newValues = [...block.values];
        newValues[editingItemIndex] = editedValue;
        updateBlock(sectionIndex, blockIndex, {
            ...block,
            values: newValues,
        });
        setEditingItemIndex(null);
        setEditedValue('');
    };

    const handleCancelEdit = () => {
        setEditingItemIndex(null);
        setEditedValue('');
    };

    const handleRemoveItem = (itemIndex) => {
        setItemToRemove(itemIndex);
        setConfirmOpen(true);
    };

    const handleConfirmRemove = () => {
        const newValues = block.values.filter((_, index) => index !== itemToRemove);
        updateBlock(sectionIndex, blockIndex, {
            ...block,
            values: newValues,
        });
        setConfirmOpen(false);
        setItemToRemove(null);
    };

    const handleAddItem = () => {
        const newValues = [...block.values, ''];
        updateBlock(sectionIndex, blockIndex, {
            ...block,
            values: newValues,
        });
        // Start editing the new item immediately
        setEditingItemIndex(newValues.length - 1);
        setEditedValue('');
    };

    return (
        <Box
            sx={{
                position: 'relative',
                border: '1px dashed rgba(0, 4, 83, 0.2)',
                borderRadius: 1,
                padding: 1,
                mb: 1,
                '&:hover': {
                    backgroundColor: 'rgba(0, 4, 83, 0.02)',
                },
            }}
        >
            <List
                sx={{
                    '@media print': {
                        pageBreakInside: 'avoid',
                        pageBreakBefore: 'auto',
                        py: 2,
                        display: 'inline',
                    },
                }}
            >
                {block.values.map((item, itemIndex) => (
                    <ListItem
                        key={itemIndex}
                        onMouseEnter={() => setHoveredItemIndex(itemIndex)}
                        onMouseLeave={() => setHoveredItemIndex(null)}
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                            padding: '2px 8px',
                            border:
                                hoveredItemIndex === itemIndex || editingItemIndex === itemIndex
                                    ? '1px solid rgba(0, 4, 83, 0.3)'
                                    : '1px solid transparent',
                            borderRadius: 1,
                            '@media print': {
                                pageBreakInside: 'avoid',
                                pageBreakBefore: 'auto',
                            },
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: '32px', alignSelf: 'flex-start', marginTop: '10px' }}>
                            <FiberManualRecordIcon style={{ fontSize: '12px' }} />
                        </ListItemIcon>

                        {editingItemIndex === itemIndex ? (
                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TextField
                                    fullWidth
                                    multiline
                                    value={editedValue}
                                    onChange={(e) => setEditedValue(e.target.value)}
                                    variant="outlined"
                                    size="small"
                                    autoFocus
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: 'rgba(255, 255, 200, 0.2)',
                                        },
                                    }}
                                />
                                <IconButton size="small" onClick={handleSaveEdit} color="primary">
                                    <CheckIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={handleCancelEdit}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ) : (
                            <>
                                <ListItemText primary={item || <em style={{ color: '#999' }}>Empty item</em>} />
                                {hoveredItemIndex === itemIndex && (
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleStartEdit(itemIndex)}
                                            sx={{ padding: '4px' }}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleRemoveItem(itemIndex)}
                                            sx={{ padding: '4px' }}
                                            color="error"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                )}
                            </>
                        )}
                    </ListItem>
                ))}
            </List>

            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                <IconButton
                    size="small"
                    onClick={handleAddItem}
                    sx={{
                        border: '1px dashed rgba(0, 4, 83, 0.3)',
                        borderRadius: 1,
                        '&:hover': {
                            backgroundColor: 'rgba(0, 4, 83, 0.05)',
                        },
                    }}
                >
                    <AddIcon fontSize="small" />
                </IconButton>
            </Box>

            <ConfirmRemoveDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleConfirmRemove}
                title="Remove this item?"
                message="This cannot be undone unless you exit without saving."
            />
        </Box>
    );
};

export default EditableList;
